'use client';

import { ArrowLeft, Search } from 'lucide-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { PROCESSOR_TYPE_OPTIONS } from '@/app/processors/create/processor-metadata';
import { SetupExperienceProcessorPicker } from '@/components/setup/experience/SetupExperienceProcessorPicker';
import { SetupProcessorEditEmbed } from '@/components/setup/experience/SetupProcessorEditEmbed';
import {
  buildSetupProcessorCreateInputForType,
  isSetupProviderType,
} from '@/components/setup/experience/lib/setupPaymentUtils';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useCreateProcessor } from '@/lib/api/processors';
import { cn } from '@/lib/utils';
import { parseTRPCError, type ParsedTRPCError } from '@/lib/utils/trpc-error-utils';

type SetupPaymentAddProcessorStep = 'pick' | 'configure';

export interface SetupPaymentProcessorConfiguredSelection {
  processorId: string;
  processorType: string;
}

interface SetupPaymentAddProcessorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onProcessorConfigured: (selection: SetupPaymentProcessorConfiguredSelection) => void;
}

export function SetupPaymentAddProcessorDialog({
  open,
  onOpenChange,
  onProcessorConfigured,
}: SetupPaymentAddProcessorDialogProps) {
  const { t } = useTranslation(['setup', 'processors']);
  const xp = 'experience.payment.addProcessorModal';
  const createProcessor = useCreateProcessor();

  const [step, setStep] = React.useState<SetupPaymentAddProcessorStep>('pick');
  const [searchQuery, setSearchQuery] = React.useState('');
  const [creatingType, setCreatingType] = React.useState<string | null>(null);
  const [formError, setFormError] = React.useState<ParsedTRPCError | null>(null);
  const [activeProcessor, setActiveProcessor] = React.useState<{ id: string; type: string } | null>(null);

  const filteredProcessors = React.useMemo(
    () =>
      PROCESSOR_TYPE_OPTIONS.filter(
        (processor) =>
          isSetupProviderType(processor.value) &&
          processor.label.toLowerCase().includes(searchQuery.trim().toLowerCase()),
      ),
    [searchQuery],
  );

  const resetState = React.useCallback(() => {
    setStep('pick');
    setSearchQuery('');
    setCreatingType(null);
    setFormError(null);
    setActiveProcessor(null);
  }, []);

  React.useEffect(() => {
    if (!open) {
      resetState();
    }
  }, [open, resetState]);

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && creatingType) {
      return;
    }
    onOpenChange(nextOpen);
  };

  const handlePickProcessor = async (type: string) => {
    setFormError(null);
    setCreatingType(type);

    try {
      const result = await createProcessor.mutateAsync(buildSetupProcessorCreateInputForType(type));
      const processorId = result[0]?.processor?.id;

      if (!processorId) {
        throw new Error(t('processors:processorCreationFailed'));
      }

      setActiveProcessor({ id: processorId, type });
      setStep('configure');
    } catch (error) {
      setFormError(parseTRPCError(error));
    } finally {
      setCreatingType(null);
    }
  };

  const handleConfigureCancel = () => {
    setActiveProcessor(null);
    setStep('pick');
  };

  const handleConfigureComplete = () => {
    if (!activeProcessor) return;

    const selection = { processorId: activeProcessor.id, processorType: activeProcessor.type };
    onOpenChange(false);
    onProcessorConfigured(selection);
  };

  const isBusy = Boolean(creatingType);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        responsive="sheet"
        {...(step === 'configure' ? { 'data-scroll-lock-scrollable': '' } : {})}
        className={cn(
          'flex max-h-[min(72dvh,640px)] min-h-0 w-[min(96vw,1320px)] max-w-[min(96vw,1320px)] flex-col gap-0 p-0 sm:w-[min(96vw,1320px)] sm:max-w-[min(96vw,1320px)]',
          step === 'configure'
            ? 'setup-xp-processor-dialog--configure max-h-[min(88dvh,820px)] overflow-y-auto overflow-x-hidden [-webkit-overflow-scrolling:touch] [overscroll-behavior:contain]'
            : 'overflow-hidden sm:max-h-[min(72dvh,640px)]',
        )}
      >
        <DialogHeader className="shrink-0 space-y-0 border-b border-[color-mix(in_oklch,var(--border)_65%,transparent)] px-5 pb-4 pt-5 text-left sm:px-6 sm:pt-6">
          <div className="mb-3 flex items-start justify-between gap-4">
            {step === 'configure' ? (
              <button
                type="button"
                className="hover:enabled:text-foreground inline-flex cursor-pointer items-center gap-1.5 border-0 bg-none p-0 text-[0.8125rem] font-medium text-[color-mix(in_oklch,var(--muted-foreground)_92%,transparent)]"
                onClick={handleConfigureCancel}
                disabled={isBusy}
              >
                <ArrowLeft className="size-4" strokeWidth={2} aria-hidden />
                <span>{t(`${xp}.backToPicker`)}</span>
              </button>
            ) : (
              <p className="inline-flex w-fit rounded-full bg-[color-mix(in_srgb,var(--primary)_10%,transparent)] px-[0.5625rem] py-[0.1875rem] text-[0.625rem] font-semibold uppercase tracking-[0.12em] text-[color-mix(in_oklch,var(--primary)_88%,var(--foreground))]">
                {t(`${xp}.kicker`)}
              </p>
            )}

            <div className="relative mr-10 w-[min(100%,14rem)] flex-[0_0_auto]">
              {step === 'pick' ? (
                <>
                  <Search
                    className="pointer-events-none absolute left-3 top-1/2 z-[1] h-4 w-4 -translate-y-1/2 text-[color-mix(in_oklch,var(--muted-foreground)_88%,transparent)]"
                    aria-hidden
                    strokeWidth={1.75}
                  />
                  <Input
                    type="search"
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                    placeholder={t('processors:createPage.searchPlaceholder')}
                    className="!bg-background !h-9 !rounded-lg !pl-9"
                    aria-label={t('processors:createPage.searchPlaceholder')}
                  />
                </>
              ) : null}
            </div>
          </div>

          <DialogTitle className="mt-0.5 text-base font-semibold leading-snug tracking-tight">
            {step === 'pick' ? t(`${xp}.title`) : t(`${xp}.configureTitle`)}
          </DialogTitle>
          <DialogDescription className="text-sm leading-relaxed">
            {step === 'pick' ? t(`${xp}.subtitle`) : t(`${xp}.configureSubtitle`)}
          </DialogDescription>
        </DialogHeader>

        <div
          className={cn(
            'min-h-0 flex-1 overflow-y-auto px-5 pb-5 pt-4 sm:px-6 sm:pb-6',
            step === 'configure' && '!overflow-visible !p-0',
          )}
        >
          {formError ? (
            <p
              className="text-destructive m-0 mb-3 rounded-md bg-[color-mix(in_srgb,var(--destructive)_8%,var(--background))] px-3 py-2.5 text-xs"
              role="alert"
            >
              {formError.message}
            </p>
          ) : null}

          {step === 'pick' ? (
            <SetupExperienceProcessorPicker
              options={filteredProcessors}
              onContinue={handlePickProcessor}
              creatingType={creatingType}
            />
          ) : activeProcessor ? (
            <SetupProcessorEditEmbed
              processorId={activeProcessor.id}
              processorType={activeProcessor.type}
              onCancel={handleConfigureCancel}
              onComplete={handleConfigureComplete}
            />
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
